// The typed value beside the words on every offer term.
//
// CONSUMER: shared (the offer-summary route, the review panel, the
// acceptance carry, the merge builder, and the golden fixture).
//
// Grant, 2026-11-04 (the offer-writing program, session 1). An offer's
// terms were one display string per slot — "$600, paid by seller", "21
// days from contract acceptance", "10%" — and nothing downstream could
// compute from them. The purchase contract's close-of-escrow line is
// three boxes (month, day, year). The warranty line on page 8 is five
// (ordered by, paid by, cap, plan, company). The concession line needs
// to know whether 3 is dollars or a percent. The acceptance carry had
// to refuse anything with a % sign in it. The key-dates calendar had
// nothing to count from.
//
// This module reads the words into ONE canonical typed value per slot
// and formats a typed value back into words. Two rules decide every
// line of it:
//
// 1. THE WORDS ARE KEPT; THE TYPED VALUE SITS BESIDE THEM. Nate
//    accepted the words. The review mechanism is checking a number
//    against what he said. A typed value is what the OS read OUT of
//    those words, stored next to them so "why does line 101 say $5,000"
//    still answers with a sentence in his voice.
//
// 2. WHAT CANNOT BE READ IS NOT READ. A string with two numbers, a range,
//    a unit nobody said, a date with no year the calendar can place —
//    each comes back as a named problem ("Couldn't read this as a
//    date"), the words stay, and the screen says so. A parser that
//    picks the plausible reading is how a wrong number acquires an air
//    of authority. Nothing here does.
//
// Pure and dependency-free, like date-parts.ts and offer-slots.ts: the
// browser, the Worker and the node-pool tests all read a term the same
// way. Dates are done by hand on UTC day numbers rather than through a
// local `Date`, because `new Date("2026-12-15")` is midnight UTC and is
// December 14 in Arizona.

import { datePartsFromText } from "./date-parts";
import { findOfferSlot, matchEnumOption, type OfferSlot } from "./offer-slots";

// ─── The typed shapes ──────────────────────────────────────────────

/** Buyer or Seller, the two parties an AAR box ever offers. */
export type OfferParty = "buyer" | "seller";

/** What the OS read out of a term's words. One shape per slot type;
 *  `kind` always matches the slot's `type` in the catalog, which is
 *  what lets a reader check it belongs where it is stored. */
export type OfferTermTyped =
  | { kind: "money"; cents: number }
  | { kind: "integer_days"; days: number }
  | { kind: "boolean"; value: boolean }
  | { kind: "enum"; option: string }
  | { kind: "text"; text: string }
  | { kind: "money_or_percent"; unit: "amount"; cents: number }
  | { kind: "money_or_percent"; unit: "percent"; basis_points: number }
  | { kind: "date"; iso: string }
  /** A number of days counted from an anchor. `computed_iso` is the
   *  date that gives ONLY when the anchor date is known (acceptance has
   *  happened, or the anchor is today); null is "not yet", never a
   *  guess. */
  | {
      kind: "duration";
      days: number;
      anchor: "acceptance" | "today";
      computed_iso: string | null;
    }
  /** A date with a time of day in Arizona. `time` is "HH:MM" (24h) or
   *  null when no time was said — the box stays empty rather than
   *  taking a default. */
  | { kind: "datetime"; date: string; time: string | null; time_zone: "America/Phoenix" }
  | {
      kind: "home_warranty";
      declined: boolean;
      ordered_by: OfferParty | null;
      paid_by: OfferParty | null;
      cap_cents: number | null;
      plan: string | null;
      company: string | null;
    }
  | { kind: "possession"; at_coe: boolean; alternate_terms: string | null }
  | { kind: "people"; people: { name: string; email: string | null }[] }
  | {
      kind: "sale_of_home_contingency";
      contingent: boolean;
      address: string | null;
      under_contract: boolean | null;
      coe_date: string | null;
      deadline: string | null;
    }
  | {
      kind: "escalation";
      start_cents: number | null;
      step_cents: number | null;
      cap_cents: number | null;
    }
  | { kind: "lease_back"; days: number | null; rent_cents: number | null };

/** What the parser needs to know about the world. */
export interface OfferTermReadContext {
  /** Today's date in Arizona, ISO (2026-11-04). Relative phrasings
   *  ("tomorrow at noon", "Friday 5pm") resolve against it. */
  today: string;
  /** The day the seller accepted, ISO, once known. A duration anchored
   *  on acceptance gets its computed date only from this. */
  acceptanceDate?: string | null;
}

export type OfferTermReadResult =
  | { ok: true; typed: OfferTermTyped }
  | {
      ok: false;
      /** Plain English, shown verbatim: "Couldn't read this as a date". */
      problem: string;
    };

export const TIME_ZONE = "America/Phoenix" as const;

// ─── Day arithmetic without a local Date ───────────────────────────

const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
] as const;

const WEEKDAYS = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
] as const;

function isoToDayNumber(iso: string): number | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (!m) return null;
  const t = Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return Math.floor(t / 86_400_000);
}

function dayNumberToIso(day: number): string {
  const d = new Date(day * 86_400_000);
  const y = d.getUTCFullYear();
  const mo = String(d.getUTCMonth() + 1).padStart(2, "0");
  const da = String(d.getUTCDate()).padStart(2, "0");
  return `${y}-${mo}-${da}`;
}

/** ISO date plus N days, or null when the input is not an ISO date. */
export function addDaysIso(iso: string, days: number): string | null {
  const n = isoToDayNumber(iso);
  if (n === null) return null;
  return dayNumberToIso(n + days);
}

function weekdayIndex(iso: string): number | null {
  const n = isoToDayNumber(iso);
  if (n === null) return null;
  // 1970-01-01 was a Thursday (4).
  return (((n + 4) % 7) + 7) % 7;
}

/** "December 15, 2026" from "2026-12-15"; the words a form wants. */
export function formatIsoDate(iso: string): string {
  const parts = datePartsFromText(iso);
  if (!parts) return iso;
  return `${parts.month} ${parts.day}, ${parts.year}`;
}

function isoFromParts(year: number, monthIndex: number, day: number): string | null {
  const text = `${year}-${String(monthIndex + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
  return datePartsFromText(text) ? text : null;
}

function monthIndexFromWord(word: string): number | null {
  const w = word.toLowerCase().replace(/\.$/, "");
  if (w.length < 3) return null;
  const i = MONTHS.findIndex((m) => m.toLowerCase().startsWith(w));
  return i >= 0 ? i : null;
}

// ─── Numbers and money ─────────────────────────────────────────────

const SMALL_NUMBER_WORDS: Record<string, number> = {
  one: 1,
  two: 2,
  three: 3,
  four: 4,
  five: 5,
  six: 6,
  seven: 7,
  eight: 8,
  nine: 9,
  ten: 10,
  eleven: 11,
  twelve: 12,
  thirteen: 13,
  fourteen: 14,
  fifteen: 15,
  twenty: 20,
  thirty: 30,
  forty: 40,
  "forty-five": 45,
  "forty five": 45,
  sixty: 60,
  ninety: 90,
};

/** Every dollar amount in the words, in cents, in order. "$475,000",
 *  "475000", "$5k", "1.2M". A bare number with no $ counts only when
 *  the caller says so, because "10" in "10 day inspection" is not money. */
function moneyMatches(text: string, opts: { bareNumbers: boolean }): number[] {
  const out: number[] = [];
  const re = /\$\s*(\d[\d,]*(?:\.\d+)?)\s*([kKmM])?(?![\d%])|(?<![\d$.,])(\d[\d,]*(?:\.\d+)?)\s*([kKmM])?\s*(?:dollars)(?![\d%])/g;
  for (const m of text.matchAll(re)) {
    const raw = (m[1] ?? m[3] ?? "").replace(/,/g, "");
    const suffix = (m[2] ?? m[4] ?? "").toLowerCase();
    const n = Number(raw);
    if (!Number.isFinite(n)) continue;
    const scaled = suffix === "k" ? n * 1_000 : suffix === "m" ? n * 1_000_000 : n;
    out.push(Math.round(scaled * 100));
  }
  if (out.length === 0 && opts.bareNumbers) {
    const bare = /^\s*(\d[\d,]*(?:\.\d+)?)\s*([kKmM])?\s*$/.exec(text);
    if (bare) {
      const n = Number((bare[1] ?? "").replace(/,/g, ""));
      const suffix = (bare[2] ?? "").toLowerCase();
      const scaled = suffix === "k" ? n * 1_000 : suffix === "m" ? n * 1_000_000 : n;
      if (Number.isFinite(n)) out.push(Math.round(scaled * 100));
    }
  }
  return out;
}

const RANGE = /\d\s*(?:-|–|to|or)\s*\$?\d/i;

/**
 * One dollar amount, in cents, or a problem.
 *
 * REFUSES a percent sign, a range ("5,000-7,500"), two amounts, and any
 * amount in words ("ten thousand") — each has more than one defensible
 * reading, and a column that took the first would be the invisible
 * wrong number the whole design exists to stop.
 */
export function readMoney(words: string): { ok: true; cents: number } | { ok: false; problem: string } {
  const text = words.trim();
  if (!text) return { ok: false, problem: "Nothing to read." };
  if (text.includes("%") || /\bpercent\b/i.test(text)) {
    return { ok: false, problem: "Couldn't read this as a dollar amount — it's a percentage." };
  }
  if (RANGE.test(text)) return { ok: false, problem: "Couldn't read this as one dollar amount — it's a range." };
  const found = moneyMatches(text, { bareNumbers: true });
  if (found.length === 0) return { ok: false, problem: "Couldn't read this as a dollar amount." };
  if (found.length > 1) return { ok: false, problem: "Couldn't read this as one dollar amount — it has two." };
  const cents = found[0] ?? 0;
  if (cents < 0) return { ok: false, problem: "Couldn't read this as a dollar amount." };
  return { ok: true, cents };
}

/** A percentage, in basis points (3.5% = 350), or a problem. */
export function readPercent(words: string): { ok: true; basisPoints: number } | { ok: false; problem: string } {
  const text = words.trim();
  const m = /(?<![\d.])(\d+(?:\.\d+)?)\s*(?:%|percent|pct)\b/i.exec(text) ?? /(\d+(?:\.\d+)?)\s*%/.exec(text);
  if (!m) return { ok: false, problem: "Couldn't read this as a percentage." };
  if (RANGE.test(text)) return { ok: false, problem: "Couldn't read this as one percentage — it's a range." };
  const n = Number(m[1]);
  if (!Number.isFinite(n) || n < 0 || n > 100) {
    return { ok: false, problem: "Couldn't read this as a percentage." };
  }
  return { ok: true, basisPoints: Math.round(n * 100) };
}

/** Dollars in words: "$475,000", "$600", "$3,500.50". */
export function formatCents(cents: number): string {
  const whole = Math.trunc(Math.abs(cents) / 100);
  const frac = Math.abs(cents) % 100;
  const grouped = whole.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  const sign = cents < 0 ? "-" : "";
  return frac === 0 ? `${sign}$${grouped}` : `${sign}$${grouped}.${String(frac).padStart(2, "0")}`;
}

/** "3%", "3.5%", "0.25%" — never more digits than were said. */
export function formatBasisPoints(bp: number): string {
  const pct = bp / 100;
  return `${Number.isInteger(pct) ? pct.toFixed(0) : String(pct)}%`;
}

function readSmallCount(words: string): number | null {
  const text = words.trim().toLowerCase();
  const digits = /(?<![\d.])(\d{1,3})(?![\d.])/.exec(text);
  if (digits) return Number(digits[1]);
  for (const [word, n] of Object.entries(SMALL_NUMBER_WORDS)) {
    if (new RegExp(`\\b${word}\\b`).test(text)) return n;
  }
  return null;
}

// ─── Dates, durations and times ────────────────────────────────────

const ANCHOR_WORDS = /\b(?:after|from|of|following|post)\s+(?:contract\s+)?(?:acceptance|accepted|the\s+contract|mutual\s+acceptance|ratification)\b|\bfrom\s+(?:the\s+)?(?:contract\s+)?(?:acceptance|execution)\b/i;
// Only an EXPLICIT "from today" moves the anchor. "Close in 21 days"
// means 21 days from acceptance — that is how the AAR contract counts
// a closing window, and an agent saying it out loud means the same.
const TODAY_ANCHOR = /\b(?:from|after|of)\s+(?:today|now)\b/i;

/** A span of days, with its anchor. "21 days after acceptance", "close
 *  in 21 days", "30 days" (the AAR convention: from acceptance). */
function readDuration(
  words: string,
  ctx: OfferTermReadContext,
): { kind: "duration"; days: number; anchor: "acceptance" | "today"; computed_iso: string | null } | null {
  const text = words.trim();
  const m = /(?<![\d.])(\d{1,3})\s*(?:-|\s)?\s*(?:calendar\s+|business\s+)?days?\b/i.exec(text);
  let days: number | null = m ? Number(m[1]) : null;
  if (days === null) {
    const wordy = /\b([a-z]+(?:[\s-][a-z]+)?)\s+days?\b/i.exec(text);
    if (wordy) days = SMALL_NUMBER_WORDS[(wordy[1] ?? "").toLowerCase()] ?? null;
  }
  if (days === null || !Number.isInteger(days) || days < 0) return null;
  if (/\bbusiness\s+days?\b/i.test(text)) return null; // the contract counts calendar days; refused rather than converted
  const anchor: "acceptance" | "today" = TODAY_ANCHOR.test(text) && !ANCHOR_WORDS.test(text) ? "today" : "acceptance";
  const from = anchor === "today" ? ctx.today : (ctx.acceptanceDate ?? null);
  return { kind: "duration", days, anchor, computed_iso: from ? addDaysIso(from, days) : null };
}

/** Strip a time-of-day clause so the date part can be read alone. */
const TIME_CLAUSE =
  /(?:\s*,?\s*(?:at|@)\s+)?\b(?:(\d{1,2})(?::(\d{2}))?\s*(a\.?m\.?|p\.?m\.?)|(\d{1,2}):(\d{2})\b|(noon|midnight))(?:\s*(?:,\s*)?(?:mountain\s+standard\s+time|mst|arizona\s+time|az\s+time))?/i;

function readTime(words: string): { time: string | null; rest: string; said: boolean } {
  const m = TIME_CLAUSE.exec(words);
  if (!m) return { time: null, rest: words, said: false };
  let hours: number;
  let minutes = 0;
  if (m[6]) {
    hours = m[6].toLowerCase() === "noon" ? 12 : 0;
  } else if (m[3]) {
    hours = Number(m[1]);
    minutes = Number(m[2] ?? 0);
    const pm = m[3].toLowerCase().startsWith("p");
    if (hours === 12) hours = pm ? 12 : 0;
    else if (pm) hours += 12;
  } else {
    hours = Number(m[4]);
    minutes = Number(m[5] ?? 0);
  }
  if (hours > 23 || minutes > 59) return { time: null, rest: words, said: false };
  const rest = (words.slice(0, m.index) + " " + words.slice(m.index + m[0].length)).replace(/\s+/g, " ").trim();
  return { time: `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`, rest, said: true };
}

/**
 * A calendar date, ISO, from the ways a person writes one — or from
 * the ways they SAY one, given today: "tomorrow", "Friday", "next
 * Friday", "November 6" (the next November 6), "Nov 6 2026",
 * "11/6/2026", "2026-11-06".
 */
export function readDate(words: string, ctx: OfferTermReadContext): { ok: true; iso: string } | { ok: false; problem: string } {
  let text = words
    .trim()
    .replace(/^(?:on|by|the)\s+/i, "")
    .replace(/[.,;]+$/, "")
    .replace(/(\d)(?:st|nd|rd|th)\b/g, "$1")
    .trim();
  if (!text) return { ok: false, problem: "Couldn't read this as a date." };

  const direct = datePartsFromText(text);
  if (direct) {
    const iso = isoFromParts(Number(direct.year), MONTHS.indexOf(direct.month as (typeof MONTHS)[number]), Number(direct.day));
    return iso ? { ok: true, iso } : { ok: false, problem: "Couldn't read this as a date." };
  }

  const lower = text.toLowerCase();
  if (lower === "today") return { ok: true, iso: ctx.today };
  if (lower === "tomorrow") {
    const iso = addDaysIso(ctx.today, 1);
    return iso ? { ok: true, iso } : { ok: false, problem: "Couldn't read this as a date." };
  }
  if (lower === "day after tomorrow" || lower === "the day after tomorrow") {
    const iso = addDaysIso(ctx.today, 2);
    return iso ? { ok: true, iso } : { ok: false, problem: "Couldn't read this as a date." };
  }

  // "Friday", "this Friday", "next Friday": the next one after today.
  // Today's own weekday means a week out — "Friday" said on a Friday
  // is never today — and "next" says the same thing, never a week
  // further, because agents use both for the coming one.
  const weekdayOnly = /^(?:this\s+|next\s+|coming\s+)?(sunday|monday|tuesday|wednesday|thursday|friday|saturday)$/i.exec(lower);
  if (weekdayOnly) {
    const target = WEEKDAYS.findIndex((w) => w.toLowerCase() === (weekdayOnly[1] ?? "").toLowerCase());
    const todayIndex = weekdayIndex(ctx.today);
    if (target < 0 || todayIndex === null) return { ok: false, problem: "Couldn't read this as a date." };
    const ahead = ((target - todayIndex + 7) % 7) || 7;
    const iso = addDaysIso(ctx.today, ahead);
    return iso ? { ok: true, iso } : { ok: false, problem: "Couldn't read this as a date." };
  }

  // A weekday in front of a real date is decoration: "Friday November 6
  // at 5pm". Dropped, then re-checked below.
  text = text.replace(/^(?:sunday|monday|tuesday|wednesday|thursday|friday|saturday),?\s+/i, "").trim();

  // "November 6, 2026" / "Nov 6 2026" / "6 November 2026" / "11/6/2026".
  const worded = /^([a-z]+\.?)\s+(\d{1,2}),?\s+(\d{4})$/i.exec(text) ?? null;
  if (worded) {
    const mi = monthIndexFromWord(worded[1] ?? "");
    const iso = mi === null ? null : isoFromParts(Number(worded[3]), mi, Number(worded[2]));
    return iso ? { ok: true, iso } : { ok: false, problem: "Couldn't read this as a date." };
  }
  const dayFirst = /^(\d{1,2})\s+([a-z]+\.?),?\s+(\d{4})$/i.exec(text);
  if (dayFirst) {
    const mi = monthIndexFromWord(dayFirst[2] ?? "");
    const iso = mi === null ? null : isoFromParts(Number(dayFirst[3]), mi, Number(dayFirst[1]));
    return iso ? { ok: true, iso } : { ok: false, problem: "Couldn't read this as a date." };
  }
  const slashed = /^(\d{1,2})\/(\d{1,2})\/(\d{2,4})$/.exec(text);
  if (slashed) {
    const yRaw = Number(slashed[3]);
    const y = yRaw < 100 ? 2000 + yRaw : yRaw;
    const iso = isoFromParts(y, Number(slashed[1]) - 1, Number(slashed[2]));
    return iso ? { ok: true, iso } : { ok: false, problem: "Couldn't read this as a date." };
  }

  // No year said: the next time that month and day come round, from
  // today. An offer never names a date in the past.
  const noYear = /^([a-z]+\.?)\s+(\d{1,2})$/i.exec(text) ?? null;
  const noYearSlashed = /^(\d{1,2})\/(\d{1,2})$/.exec(text);
  const mi = noYear ? monthIndexFromWord(noYear[1] ?? "") : noYearSlashed ? Number(noYearSlashed[1]) - 1 : null;
  const day = noYear ? Number(noYear[2]) : noYearSlashed ? Number(noYearSlashed[2]) : null;
  if (mi !== null && day !== null) {
    const todayYear = Number(ctx.today.slice(0, 4));
    for (const year of [todayYear, todayYear + 1]) {
      const iso = isoFromParts(year, mi, day);
      if (iso && iso >= ctx.today) return { ok: true, iso };
    }
    return { ok: false, problem: "Couldn't read this as a date." };
  }

  return { ok: false, problem: "Couldn't read this as a date." };
}

/** A date with its time: "November 6, 2026 at 5:00 PM", "tomorrow at
 *  noon", "Friday 5pm". No time said is allowed and recorded as such. */
export function readDateTime(
  words: string,
  ctx: OfferTermReadContext,
): { ok: true; typed: Extract<OfferTermTyped, { kind: "datetime" }> } | { ok: false; problem: string } {
  const { time, rest } = readTime(words);
  const date = readDate(rest.replace(/\s+(?:at|@)\s*$/i, ""), ctx);
  if (!date.ok) return { ok: false, problem: "Couldn't read this as a date and time." };
  return { ok: true, typed: { kind: "datetime", date: date.iso, time, time_zone: TIME_ZONE } };
}

function formatTime(time: string): string {
  const [h, m] = time.split(":").map(Number);
  const hours = h ?? 0;
  const minutes = m ?? 0;
  const suffix = hours >= 12 ? "PM" : "AM";
  const twelve = hours % 12 === 0 ? 12 : hours % 12;
  return `${twelve}:${String(minutes).padStart(2, "0")} ${suffix}`;
}

// ─── The structured slots ──────────────────────────────────────────

function partyIn(words: string): OfferParty | null {
  const m = /\b(buyer|seller|purchaser|owner)s?\b/i.exec(words);
  if (!m) return null;
  const w = (m[1] ?? "").toLowerCase();
  return w === "buyer" || w === "purchaser" ? "buyer" : "seller";
}

function firstMoney(words: string): number | null {
  const found = moneyMatches(words, { bareNumbers: false });
  return found.length > 0 ? (found[0] ?? null) : null;
}

/**
 * A home warranty's parts. The AAR line reads: "A Home Warranty Plan
 * will be ordered by [Buyer|Seller] ... at a cost not to exceed $___ to
 * be paid for by [Buyer|Seller]".
 *
 * THE ONE RULE THAT IS A CONVENTION, NOT A PARSE: "$600 seller
 * warranty" — one party named, no verb — means the seller orders it AND
 * pays for it. That is what an Arizona agent means by it and what the
 * form is ticked as every time. A party named with "paid by" alone sets
 * paid-by; ordered-by then follows paid-by, because a warranty paid for
 * by the seller and ordered by the buyer is a term nobody says by
 * accident, and the screen shows both parts so the one time it is meant
 * it is seen.
 */
export function readHomeWarranty(words: string): Extract<OfferTermTyped, { kind: "home_warranty" }> | null {
  const text = words.trim();
  if (!text) return null;
  if (/\b(declin|waiv|none|no home warranty|no warranty|not requested)/i.test(text)) {
    return { kind: "home_warranty", declined: true, ordered_by: null, paid_by: null, cap_cents: null, plan: null, company: null };
  }
  const cap = firstMoney(text);
  const orderedMatch = /\border(?:ed)?\s+by\s+(?:the\s+)?(buyer|seller)/i.exec(text);
  const paidMatch =
    /\b(?:paid|pay|pays|payable)(?:\s+for)?\s+by\s+(?:the\s+)?(buyer|seller)/i.exec(text) ??
    /\b(buyer|seller)(?:\s+to)?\s+pays?\b/i.exec(text) ??
    /\b(buyer|seller)[-\s]paid\b/i.exec(text);
  let orderedBy: OfferParty | null = orderedMatch ? ((orderedMatch[1] ?? "").toLowerCase() as OfferParty) : null;
  let paidBy: OfferParty | null = paidMatch ? ((paidMatch[1] ?? "").toLowerCase() as OfferParty) : null;
  if (!orderedBy && !paidBy) {
    // One party, no verb: that party orders and pays.
    const only = partyIn(text);
    if (only) {
      orderedBy = only;
      paidBy = only;
    }
  } else if (!orderedBy && paidBy) {
    orderedBy = paidBy;
  } else if (orderedBy && !paidBy) {
    paidBy = orderedBy;
  }
  const planMatch = /\b(?:coverage|plan)\s*(?::|of|is)?\s*([A-Za-z][^,;]*?)(?=\s*(?:,|;|$|\bissued\b|\bthrough\b|\bby\b))/i.exec(text);
  const companyMatch = /\b(?:issued\s+by|through|from|with|company)\s*(?::)?\s*([A-Z][\w&' .-]*?)(?=\s*(?:,|;|$|\bup to\b|\bnot to exceed\b|\bat a cost\b))/.exec(text);
  const plan = planMatch ? (planMatch[1] ?? "").trim() || null : null;
  const companyRaw = companyMatch ? (companyMatch[1] ?? "").trim() : "";
  const company = companyRaw && !/^(buyer|seller)s?$/i.test(companyRaw) ? companyRaw : null;
  if (!orderedBy && !paidBy && cap === null && !plan && !company) return null;
  return { kind: "home_warranty", declined: false, ordered_by: orderedBy, paid_by: paidBy, cap_cents: cap, plan, company };
}

export function readPossession(words: string): Extract<OfferTermTyped, { kind: "possession" }> | null {
  const text = words.trim();
  if (!text) return null;
  const mentionsCoe = /\b(?:close of escrow|coe|closing|at close|upon close|at recording|upon recording|when it records)\b/i.test(text);
  const hasOffset = /\b\d+\s*(?:days?|hours?)\b/i.test(text) || /\b(?:after|before|following|prior to|post)\b/i.test(text);
  if (mentionsCoe && !hasOffset) return { kind: "possession", at_coe: true, alternate_terms: null };
  return { kind: "possession", at_coe: false, alternate_terms: text };
}

const EMAIL = /[\w.+-]+@[\w-]+(?:\.[\w-]+)+/;

export function readPeople(words: string): Extract<OfferTermTyped, { kind: "people" }> | null {
  const people: { name: string; email: string | null }[] = [];
  for (const chunk of words.split(/\s*;\s*|\s+and\s+(?=[A-Z])|\s*\n\s*/)) {
    const piece = chunk.trim();
    if (!piece) continue;
    const email = EMAIL.exec(piece)?.[0] ?? null;
    const name = piece
      .replace(EMAIL, "")
      .replace(/[<>()\[\],]+/g, " ")
      .replace(/\b(?:second|third|additional|co-?)\s*buyer\b:?/i, "")
      .replace(/\bbuyer\s*\d\b:?/i, "")
      .replace(/\s+/g, " ")
      .trim();
    if (!name && !email) continue;
    people.push({ name: name || (email ?? ""), email });
  }
  return people.length > 0 ? { kind: "people", people } : null;
}

export function readSaleOfHomeContingency(
  words: string,
  ctx: OfferTermReadContext,
): Extract<OfferTermTyped, { kind: "sale_of_home_contingency" }> | null {
  const text = words.trim();
  if (!text) return null;
  const notContingent = /\b(?:not contingent|no contingency|none|no\b|waived)/i.test(text) && !/\bcontingent on\b/i.test(text);
  const addressMatch = /\b(?:at|of|on|located at|home at|property at|house at)\s+(\d+\s+[^,;]+?)(?=\s*(?:,|;|$|\bwhich\b|\bthat\b|\bunder\b|\bclos\w*\b|\bby\b|\bdeadline\b))/i.exec(text);
  const address = addressMatch ? (addressMatch[1] ?? "").trim() : null;
  const underContract = /\bnot\s+(?:yet\s+)?under contract\b/i.test(text)
    ? false
    : /\bunder contract\b/i.test(text)
      ? true
      : /\b(?:not (?:yet )?listed|to be listed|will list)\b/i.test(text)
        ? false
        : null;
  const coeMatch = /\b(?:clos(?:ing|es|e of escrow|e)|coe)\s+(?:on\s+|date\s+(?:of\s+)?)?([A-Za-z]+\.?\s+\d{1,2}(?:,?\s+\d{4})?|\d{1,2}\/\d{1,2}(?:\/\d{2,4})?)/i.exec(text);
  const deadlineMatch = /\b(?:by|deadline(?:\s+of)?|no later than)\s+([A-Za-z]+\.?\s+\d{1,2}(?:,?\s+\d{4})?|\d{1,2}\/\d{1,2}(?:\/\d{2,4})?)/i.exec(text);
  const coe = coeMatch ? readDate(coeMatch[1] ?? "", ctx) : null;
  const deadline = deadlineMatch ? readDate(deadlineMatch[1] ?? "", ctx) : null;
  return {
    kind: "sale_of_home_contingency",
    contingent: !notContingent,
    address,
    under_contract: underContract,
    coe_date: coe && coe.ok ? coe.iso : null,
    deadline: deadline && deadline.ok ? deadline.iso : null,
  };
}

export function readEscalation(words: string): Extract<OfferTermTyped, { kind: "escalation" }> | null {
  const text = words.trim();
  if (!text) return null;
  const startM = /\b(?:start(?:ing|s)?(?:\s+at)?|from|base(?:\s+of)?|offer(?:ing)?\s+of)\s+(\$[\d,]+(?:\.\d+)?[kKmM]?)/i.exec(text);
  const stepM = /(\$[\d,]+(?:\.\d+)?[kKmM]?)\s+(?:over|above|more than|higher than|increments?)|\b(?:by|increments?\s+of|step(?:s)?\s+of)\s+(\$[\d,]+(?:\.\d+)?[kKmM]?)/i.exec(text);
  const capM = /\b(?:up to|cap(?:ped)?(?:\s+at)?|not to exceed|max(?:imum)?(?:\s+of)?|ceiling(?:\s+of)?|top(?:\s+of)?)\s+(\$[\d,]+(?:\.\d+)?[kKmM]?)/i.exec(text);
  const start = startM ? firstMoney(startM[1] ?? "") : null;
  const step = stepM ? firstMoney(stepM[1] ?? stepM[2] ?? "") : null;
  const cap = capM ? firstMoney(capM[1] ?? "") : null;
  if (start === null && step === null && cap === null) return null;
  return { kind: "escalation", start_cents: start, step_cents: step, cap_cents: cap };
}

export function readLeaseBack(words: string): Extract<OfferTermTyped, { kind: "lease_back" }> | null {
  const text = words.trim();
  if (!text) return null;
  const days = readSmallCount(text.replace(/\$[\d,]+/g, ""));
  const rent = /\b(?:free|no rent|rent[- ]free|at no cost|no charge)\b/i.test(text) ? 0 : firstMoney(text);
  if (days === null && rent === null) return null;
  return { kind: "lease_back", days, rent_cents: rent };
}

// ─── The one entry point ───────────────────────────────────────────

const TRUE_WORDS = /^(?:yes|y|true|included|contingent|with|keep|required|on)\b/i;
const FALSE_WORDS = /^(?:no|n|false|waived|waive|none|without|not contingent|off|declined)\b/i;

/**
 * Read a slot's words into its typed value.
 *
 * Every branch either reads the whole thing or refuses it with a
 * sentence. Nothing reads the first plausible half of a string.
 */
export function readOfferTermValue(
  slotKey: string,
  words: string,
  ctx: OfferTermReadContext,
): OfferTermReadResult {
  const slot = findOfferSlot(slotKey);
  if (!slot) return { ok: false, problem: `"${slotKey}" isn't something an offer can say.` };
  return readForSlot(slot, words, ctx);
}

function readForSlot(slot: OfferSlot, words: string, ctx: OfferTermReadContext): OfferTermReadResult {
  const text = words.trim();
  if (!text) return { ok: false, problem: "Nothing to read." };

  switch (slot.type) {
    case "money": {
      const r = readMoney(text);
      return r.ok ? { ok: true, typed: { kind: "money", cents: r.cents } } : r;
    }
    case "money_or_percent": {
      const saysPercent = text.includes("%") || /\bpercent\b|\bpct\b/i.test(text);
      const saysDollars = text.includes("$") || /\bdollars?\b/i.test(text);
      if (saysPercent && saysDollars) {
        return { ok: false, problem: "Couldn't read this — it says both dollars and a percentage." };
      }
      if (saysPercent) {
        const r = readPercent(text);
        return r.ok
          ? { ok: true, typed: { kind: "money_or_percent", unit: "percent", basis_points: r.basisPoints } }
          : r;
      }
      if (saysDollars) {
        const r = readMoney(text);
        return r.ok ? { ok: true, typed: { kind: "money_or_percent", unit: "amount", cents: r.cents } } : r;
      }
      return { ok: false, problem: "Could be dollars or a percentage — say which." };
    }
    case "integer_days": {
      if (/\bbusiness\s+days?\b/i.test(text)) {
        return { ok: false, problem: "Couldn't read this as a number of days — the contract counts calendar days." };
      }
      const n = readSmallCount(text);
      return n === null || n < 0
        ? { ok: false, problem: "Couldn't read this as a number of days." }
        : { ok: true, typed: { kind: "integer_days", days: n } };
    }
    case "boolean": {
      if (TRUE_WORDS.test(text)) return { ok: true, typed: { kind: "boolean", value: true } };
      if (FALSE_WORDS.test(text)) return { ok: true, typed: { kind: "boolean", value: false } };
      return { ok: false, problem: "Couldn't read this as yes or no." };
    }
    case "enum": {
      const option = matchEnumOption(slot, text);
      return option
        ? { ok: true, typed: { kind: "enum", option } }
        : { ok: false, problem: `Couldn't read this as one of: ${(slot.options ?? []).join(", ")}.` };
    }
    case "text":
      return { ok: true, typed: { kind: "text", text } };
    case "date": {
      const r = readDate(text, ctx);
      return r.ok ? { ok: true, typed: { kind: "date", iso: r.iso } } : r;
    }
    case "date_or_duration": {
      const duration = readDuration(text, ctx);
      if (duration) return { ok: true, typed: duration };
      const r = readDate(text, ctx);
      return r.ok
        ? { ok: true, typed: { kind: "date", iso: r.iso } }
        : { ok: false, problem: "Couldn't read this as a date or a number of days." };
    }
    case "datetime": {
      const r = readDateTime(text, ctx);
      return r.ok ? { ok: true, typed: r.typed } : r;
    }
    case "home_warranty": {
      const typed = readHomeWarranty(text);
      return typed ? { ok: true, typed } : { ok: false, problem: "Couldn't read who orders or pays, or a cost cap." };
    }
    case "possession": {
      const typed = readPossession(text);
      return typed ? { ok: true, typed } : { ok: false, problem: "Nothing to read." };
    }
    case "people": {
      const typed = readPeople(text);
      return typed ? { ok: true, typed } : { ok: false, problem: "Couldn't read a name." };
    }
    case "sale_of_home_contingency": {
      const typed = readSaleOfHomeContingency(text, ctx);
      return typed ? { ok: true, typed } : { ok: false, problem: "Nothing to read." };
    }
    case "escalation": {
      const typed = readEscalation(text);
      return typed ? { ok: true, typed } : { ok: false, problem: "Couldn't read a starting price, a step or a cap." };
    }
    case "lease_back": {
      const typed = readLeaseBack(text);
      return typed ? { ok: true, typed } : { ok: false, problem: "Couldn't read a number of days or a rent." };
    }
    default:
      return { ok: false, problem: "Nothing reads this kind of term yet." };
  }
}

// ─── Words from a typed value ──────────────────────────────────────

const PARTY_LABEL: Record<OfferParty, string> = { buyer: "Buyer", seller: "Seller" };

/**
 * The words a typed value prints as — the canonical spelling of every
 * term, used wherever the OS writes one rather than echoes one.
 *
 * `ctx.today` is used for exactly one thing: showing what a duration
 * anchored on acceptance would come to if accepted today, labelled as
 * such. Without it the duration prints as days alone.
 */
export function formatOfferTermValue(typed: OfferTermTyped, ctx?: Partial<OfferTermReadContext>): string {
  switch (typed.kind) {
    case "money":
      return formatCents(typed.cents);
    case "integer_days":
      return `${typed.days} day${typed.days === 1 ? "" : "s"}`;
    case "boolean":
      return typed.value ? "Yes" : "No";
    case "enum":
      return typed.option;
    case "text":
      return typed.text;
    case "money_or_percent":
      return typed.unit === "amount" ? formatCents(typed.cents) : formatBasisPoints(typed.basis_points);
    case "date":
      return formatIsoDate(typed.iso);
    case "duration": {
      const head = `${typed.days} day${typed.days === 1 ? "" : "s"} after ${typed.anchor === "acceptance" ? "acceptance" : "today"}`;
      if (typed.computed_iso) return `${head} (${formatIsoDate(typed.computed_iso)})`;
      if (typed.anchor === "acceptance" && ctx?.today) {
        const ifToday = addDaysIso(ctx.today, typed.days);
        if (ifToday) return `${head} (${formatIsoDate(ifToday)} if accepted today)`;
      }
      return head;
    }
    case "datetime":
      return typed.time ? `${formatIsoDate(typed.date)} at ${formatTime(typed.time)}` : formatIsoDate(typed.date);
    case "home_warranty": {
      if (typed.declined) return "Declined";
      const parts: string[] = [];
      if (typed.ordered_by) parts.push(`ordered by ${PARTY_LABEL[typed.ordered_by]}`);
      if (typed.paid_by) parts.push(`paid by ${PARTY_LABEL[typed.paid_by]}`);
      if (typed.cap_cents !== null) parts.push(`up to ${formatCents(typed.cap_cents)}`);
      if (typed.plan) parts.push(`plan: ${typed.plan}`);
      if (typed.company) parts.push(`issued by ${typed.company}`);
      const s = parts.join(", ");
      return s ? s.charAt(0).toUpperCase() + s.slice(1) : "Home warranty";
    }
    case "possession":
      return typed.at_coe ? "At close of escrow" : (typed.alternate_terms ?? "");
    case "people":
      return typed.people.map((p) => (p.email ? `${p.name} (${p.email})` : p.name)).join("; ");
    case "sale_of_home_contingency": {
      if (!typed.contingent) return "Not contingent on the sale of the buyer's home";
      const parts: string[] = ["Contingent on the sale of the buyer's home"];
      if (typed.address) parts[0] = `Contingent on the sale of ${typed.address}`;
      if (typed.under_contract === true) parts.push("under contract");
      if (typed.under_contract === false) parts.push("not yet under contract");
      if (typed.coe_date) parts.push(`closing ${formatIsoDate(typed.coe_date)}`);
      if (typed.deadline) parts.push(`deadline ${formatIsoDate(typed.deadline)}`);
      return parts.join("; ");
    }
    case "escalation": {
      const parts: string[] = [];
      if (typed.start_cents !== null) parts.push(`starting at ${formatCents(typed.start_cents)}`);
      if (typed.step_cents !== null) parts.push(`${formatCents(typed.step_cents)} over any competing offer`);
      if (typed.cap_cents !== null) parts.push(`up to ${formatCents(typed.cap_cents)}`);
      const s = parts.join(", ");
      return s ? s.charAt(0).toUpperCase() + s.slice(1) : "Escalation clause";
    }
    case "lease_back": {
      const parts: string[] = [];
      if (typed.days !== null) parts.push(`${typed.days} day${typed.days === 1 ? "" : "s"}`);
      if (typed.rent_cents !== null) parts.push(typed.rent_cents === 0 ? "rent-free" : `at ${formatCents(typed.rent_cents)}`);
      return parts.join(" ") || "Lease-back";
    }
  }
}

/**
 * The one line a screen shows under a term's words: what the OS read
 * out of them, when that adds anything. Null when the typed value prints
 * exactly as the words were written ("$475,000" read as $475,000 needs
 * no second line); otherwise the canonical spelling, so "Friday 5pm"
 * shows "November 6, 2026 at 5:00 PM" beneath it and "21 days after
 * acceptance" shows the date that would make.
 */
export function typedReadingLine(
  typed: OfferTermTyped | null,
  words: string,
  ctx?: Partial<OfferTermReadContext>,
): string | null {
  if (!typed) return null;
  const printed = formatOfferTermValue(typed, ctx);
  const same = printed.trim().toLowerCase() === words.trim().toLowerCase();
  if (same && typed.kind !== "duration") return null;
  return printed;
}

/** The weekday of an ISO date, for telling the reader what day today is. */
export function weekdayName(iso: string): string | null {
  const i = weekdayIndex(iso);
  return i === null ? null : WEEKDAYS[i] ?? null;
}

/** Today in Arizona as an ISO date, from any instant. Arizona never
 *  observes daylight saving, so this is a fixed seven-hour offset and
 *  needs no time-zone database. */
export function phoenixTodayFrom(now: Date = new Date()): string {
  const shifted = new Date(now.getTime() - 7 * 60 * 60 * 1000);
  return shifted.toISOString().slice(0, 10);
}

// ─── Reading a stored row ──────────────────────────────────────────

const KINDS = new Set<OfferTermTyped["kind"]>([
  "money",
  "integer_days",
  "boolean",
  "enum",
  "text",
  "money_or_percent",
  "date",
  "duration",
  "datetime",
  "home_warranty",
  "possession",
  "people",
  "sale_of_home_contingency",
  "escalation",
  "lease_back",
]);

/** The typed value from a stored `typed_value_json`, or null for a row
 *  written before the column existed, for words that could not be
 *  read, or for JSON that is not one of the shapes above. */
export function parseTypedValueJson(json: string | null | undefined): OfferTermTyped | null {
  if (!json) return null;
  try {
    const v = JSON.parse(json) as { kind?: unknown };
    if (!v || typeof v !== "object" || typeof v.kind !== "string") return null;
    return KINDS.has(v.kind as OfferTermTyped["kind"]) ? (v as OfferTermTyped) : null;
  } catch {
    return null;
  }
}

/** The JSON a typed value is stored as. One spelling, so a row written
 *  by the route and one written by a later data pass compare equal. */
export function typedValueToJson(typed: OfferTermTyped | null): string | null {
  return typed ? JSON.stringify(typed) : null;
}

// ─── Composites the atlas asks for ─────────────────────────────────
//
// The purchase contract's financing section and earnest-money line each
// read several slots at once. These assemble them from typed values so
// the fixture, the carry and (in session 4) the binder all agree.

export interface FinancingSummary {
  loan_type: string | null;
  /** The loan amount: said outright, or worked out from the price and
   *  a percentage down. Null when neither is known. */
  loan_amount_cents: number | null;
  /** Where the loan amount came from, so a worked-out figure is never
   *  mistaken for a stated one. */
  loan_amount_source: "stated" | "computed_from_down_payment" | null;
  down_payment: Extract<OfferTermTyped, { kind: "money_or_percent" }> | null;
}

export function financingFrom(typedBySlot: ReadonlyMap<string, OfferTermTyped>): FinancingSummary {
  const loanType = typedBySlot.get("loan_type");
  const loanAmount = typedBySlot.get("loan_amount");
  const down = typedBySlot.get("down_payment");
  const price = typedBySlot.get("purchase_price");
  const out: FinancingSummary = {
    loan_type: loanType?.kind === "enum" ? loanType.option : null,
    loan_amount_cents: null,
    loan_amount_source: null,
    down_payment: down?.kind === "money_or_percent" ? down : null,
  };
  if (loanAmount?.kind === "money") {
    out.loan_amount_cents = loanAmount.cents;
    out.loan_amount_source = "stated";
  } else if (price?.kind === "money" && down?.kind === "money_or_percent") {
    const downCents =
      down.unit === "amount" ? down.cents : Math.round((price.cents * down.basis_points) / 10_000);
    out.loan_amount_cents = price.cents - downCents;
    out.loan_amount_source = "computed_from_down_payment";
  } else if (out.loan_type === "Cash") {
    out.loan_amount_cents = 0;
    out.loan_amount_source = "stated";
  }
  return out;
}

export interface EarnestMoneySummary {
  amount_cents: number | null;
  form: "personal_check" | "wire" | "other" | null;
  held_by: "escrow" | "broker_trust" | null;
}

export function earnestMoneyFrom(typedBySlot: ReadonlyMap<string, OfferTermTyped>): EarnestMoneySummary {
  const amount = typedBySlot.get("earnest_money");
  const form = typedBySlot.get("earnest_money_form");
  const held = typedBySlot.get("earnest_money_held_by");
  return {
    amount_cents: amount?.kind === "money" ? amount.cents : null,
    form:
      form?.kind === "enum"
        ? form.option === "Personal check"
          ? "personal_check"
          : form.option === "Wire transfer"
            ? "wire"
            : "other"
        : null,
    held_by: held?.kind === "enum" ? (held.option === "Escrow company" ? "escrow" : "broker_trust") : null,
  };
}
